import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {LeadButtonTerms} from "../../../../lead/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../../lead/components/LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../lead/components/LeadVendorRodoTerms";
import {askForPriceleadButtonCopy} from "../../../../lead/constants/lead_button_copy";
import {leadButtonCopyPlural} from "../../../../lead/constants/lead_button_copy_plural";
import {MessageType} from "../../../types/MessageType";
import {ApplicationSource} from "../../../utils/ApplicationSource";
import {useApplicationFormContext} from "../ApplicationFormContext";
import {ApplicationOptIns} from "../fields/ApplicationOptIns";
import {SelectRooms} from "../fields/SelectRooms";
import {YourData} from "../fields/YourData";
import {ApplicationRevealButtonWithWrapper} from "./ApplicationRevealButtonWithWrapper";
import {MessageAndQuestionsSection} from "./MessageAndQuestionsSection";
import {PropertyInfo} from "./PropertyInfo";
import {SubmitButton} from "./SubmitButton";
import {VendorDisplay} from "./VendorDisplay";

interface IProps {
    showEmailField: boolean;
    showPhoneField: boolean;
    showNameField: boolean;
    showAllInputFields: () => void;
    showTextAreaOnly: boolean;
    showMktEmailField: boolean;
    showMktSmsField: boolean;
    showRevealPriceButton?: boolean;
}

const submitButtonText = "Wyślij";

export const ApplicationDefaultForm = (props: IProps) => {
    const {showRevealPriceButton, showAllInputFields, showEmailField, showMktEmailField, showMktSmsField, showNameField, showPhoneField, showTextAreaOnly} =
        props;

    const {
        offer,
        property,
        vendor,
        hideRooms,
        hideSubmitButton,
        source,
        onCloseModal,
        leadIsSubmitting,
        applicationMessageLogic: {
            currentState: {messageType}
        }
    } = useApplicationFormContext();

    const {isMobile} = useUserDevice();

    const leadBtnText = property ? askForPriceleadButtonCopy : leadButtonCopyPlural;

    return (
        <div className={formWrapperStyle} data-testid={OFFER_TEST_IDS.LEAD_FORM.OFFER_LEAD_FORM}>
            <div className={leftSideStyle}>
                <div className={vendorHeaderStyle}>
                    <VendorDisplay offer={offer} onCloseModal={onCloseModal} vendor={vendor} className={vendorDisplayStyle} />
                </div>

                <PropertyInfo className={css({mb: 3})} />

                {!hideRooms && <SelectRooms label="Liczba pokoi, która mnie interesuje" className={css({mb: 2})} />}

                <YourData
                    showEmailField={showEmailField}
                    showPhoneField={showPhoneField}
                    showNameField={showNameField}
                    onShowAllInputFieldsClick={showAllInputFields}
                    className={yourDataStyle}
                />

                {!isMobile && (
                    <div className={desktopLeadTerms}>
                        <LeadRodoTerms />

                        {vendor && (
                            <LeadVendorRodoTerms
                                vendor={vendor}
                                buttonText={showRevealPriceButton ? leadBtnText : submitButtonText}
                                offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className={rightSideWrapper}>
                {messageType !== MessageType.NONE && <MessageAndQuestionsSection showMessageOnly={showTextAreaOnly} messageType={messageType} />}

                <ApplicationOptIns showMktEmailField={showMktEmailField} showMktSmsField={showMktSmsField} className={css({mb: 2})} />

                <div>
                    {!hideSubmitButton &&
                        (showRevealPriceButton ? (
                            <ApplicationRevealButtonWithWrapper leadIsSubmitting={leadIsSubmitting} leadBtnText={leadBtnText} />
                        ) : (
                            <SubmitButton
                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                                className={css({mt: 2})}
                                type="submit"
                                variant="filled_primary"
                                disabled={leadIsSubmitting}
                            >
                                {leadIsSubmitting ? <Loader /> : submitButtonText}
                            </SubmitButton>
                        ))}

                    {source === ApplicationSource.RevealedPrices && (
                        <Text variant="body_copy_1">
                            Wypełnij formularz, żeby poznać cenę tego mieszkania. Deweloper może skontaktować się z Tobą, żeby przedstawić Ci szczegóły oferty.
                        </Text>
                    )}
                </div>

                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={showRevealPriceButton ? leadBtnText : submitButtonText} />

                    {isMobile && (
                        <>
                            <LeadRodoTerms />

                            {vendor && (
                                <LeadVendorRodoTerms
                                    vendor={vendor}
                                    buttonText={showRevealPriceButton ? leadBtnText : submitButtonText}
                                    offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const formWrapperStyle = css({
    display: "flex",
    flexDirection: "column",

    md: {
        flexDirection: "row"
    }
});

const leftSideStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    p: 2,
    pb: 0,

    md: {
        p: 2,
        width: "35.4rem"
    }
});

const vendorHeaderStyle = css({
    pb: 3,
    display: "flex"
});

const vendorDisplayStyle = css({
    flex: "1 1 auto"
});

const yourDataStyle = css({
    margin: "0 0 1.2rem 0",

    md: {
        margin: "0 0 2.6rem 0"
    }
});

const desktopLeadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: "0 0 auto"
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2
});

const rightSideWrapper = css({
    p: 2,
    md: {
        backgroundColor: "var(--colors-gray-100)",
        width: "45.6rem"
    }
});
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {LeadButtonTerms} from "../../../../lead/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../../lead/components/LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../lead/components/LeadVendorRodoTerms";
import {askForPriceleadButtonCopy} from "../../../../lead/constants/lead_button_copy";
import {leadButtonCopyPlural} from "../../../../lead/constants/lead_button_copy_plural";
import {MessageType} from "../../../types/MessageType";
import {ApplicationSource} from "../../../utils/ApplicationSource";
import {useApplicationFormContext} from "../ApplicationFormContext";
import {ApplicationOptIns} from "../fields/ApplicationOptIns";
import {SelectRooms} from "../fields/SelectRooms";
import {YourData} from "../fields/YourData";
import {ApplicationRevealButtonWithWrapper} from "./ApplicationRevealButtonWithWrapper";
import {MessageAndQuestionsSection} from "./MessageAndQuestionsSection";
import {PropertyInfo} from "./PropertyInfo";
import {SubmitButton} from "./SubmitButton";
import {VendorDisplay} from "./VendorDisplay";

interface IProps {
    showEmailField: boolean;
    showPhoneField: boolean;
    showNameField: boolean;
    showAllInputFields: () => void;
    showTextAreaOnly: boolean;
    showMktEmailField: boolean;
    showMktSmsField: boolean;
    showRevealPriceButton?: boolean;
}

const submitButtonText = "Wyślij";

export const ApplicationDefaultForm = (props: IProps) => {
    const {showRevealPriceButton, showAllInputFields, showEmailField, showMktEmailField, showMktSmsField, showNameField, showPhoneField, showTextAreaOnly} =
        props;

    const {
        offer,
        property,
        vendor,
        hideRooms,
        hideSubmitButton,
        source,
        onCloseModal,
        leadIsSubmitting,
        applicationMessageLogic: {
            currentState: {messageType}
        }
    } = useApplicationFormContext();

    const {isMobile} = useUserDevice();

    const leadBtnText = property ? askForPriceleadButtonCopy : leadButtonCopyPlural;

    return (
        <div className={formWrapperStyle} data-testid={OFFER_TEST_IDS.LEAD_FORM.OFFER_LEAD_FORM}>
            <div className={leftSideStyle}>
                <div className={vendorHeaderStyle}>
                    <VendorDisplay offer={offer} onCloseModal={onCloseModal} vendor={vendor} className={vendorDisplayStyle} />
                </div>

                <PropertyInfo className={css({mb: 3})} />

                {!hideRooms && <SelectRooms label="Liczba pokoi, która mnie interesuje" className={css({mb: 2})} />}

                <YourData
                    showEmailField={showEmailField}
                    showPhoneField={showPhoneField}
                    showNameField={showNameField}
                    onShowAllInputFieldsClick={showAllInputFields}
                    className={yourDataStyle}
                />

                {!isMobile && (
                    <div className={desktopLeadTerms}>
                        <LeadRodoTerms />

                        {vendor && (
                            <LeadVendorRodoTerms
                                vendor={vendor}
                                buttonText={showRevealPriceButton ? leadBtnText : submitButtonText}
                                offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className={rightSideWrapper}>
                {messageType !== MessageType.NONE && <MessageAndQuestionsSection showMessageOnly={showTextAreaOnly} messageType={messageType} />}

                <ApplicationOptIns showMktEmailField={showMktEmailField} showMktSmsField={showMktSmsField} className={css({mb: 2})} />

                <div>
                    {!hideSubmitButton &&
                        (showRevealPriceButton ? (
                            <ApplicationRevealButtonWithWrapper leadIsSubmitting={leadIsSubmitting} leadBtnText={leadBtnText} />
                        ) : (
                            <SubmitButton
                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                                className={css({mt: 2})}
                                type="submit"
                                variant="filled_primary"
                                disabled={leadIsSubmitting}
                            >
                                {leadIsSubmitting ? <Loader /> : submitButtonText}
                            </SubmitButton>
                        ))}

                    {source === ApplicationSource.RevealedPrices && (
                        <Text variant="body_copy_1">
                            Wypełnij formularz, żeby poznać cenę tego mieszkania. Deweloper może skontaktować się z Tobą, żeby przedstawić Ci szczegóły oferty.
                        </Text>
                    )}
                </div>

                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={showRevealPriceButton ? leadBtnText : submitButtonText} />

                    {isMobile && (
                        <>
                            <LeadRodoTerms />

                            {vendor && (
                                <LeadVendorRodoTerms
                                    vendor={vendor}
                                    buttonText={showRevealPriceButton ? leadBtnText : submitButtonText}
                                    offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const formWrapperStyle = css({
    display: "flex",
    flexDirection: "column",

    md: {
        flexDirection: "row"
    }
});

const leftSideStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    p: 2,
    pb: 0,

    md: {
        p: 2,
        width: "35.4rem"
    }
});

const vendorHeaderStyle = css({
    pb: 3,
    display: "flex"
});

const vendorDisplayStyle = css({
    flex: "1 1 auto"
});

const yourDataStyle = css({
    margin: "0 0 1.2rem 0",

    md: {
        margin: "0 0 2.6rem 0"
    }
});

const desktopLeadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: "0 0 auto"
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2
});

const rightSideWrapper = css({
    p: 2,
    md: {
        backgroundColor: "var(--colors-gray-100)",
        width: "45.6rem"
    }
});
